import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Input, message, Tabs, Typography } from "antd";
import {
  CopyOutlined,
  DownloadOutlined,
  LinkOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { QRCodeSVG } from "qrcode.react";
import api from "../api/api";
import Footer from "../components/Footer";
import Header from "../components/Header";
import {
  buildGoogleReviewLink,
  buildMapsSearchUrl,
  resolveReviewLinkFromInput,
  ReviewLinkResult,
} from "../utils/googleReviewLink";
import { downloadQrCodePngFromSvg, QR_CODE_STYLE } from "../utils/qrCode";

type FreeRegisterData = {
  video_link: string;
  guide_link: string;
  guide_text: string;
  name: string;
  title: string;
  path: string;
};

const { Title, Paragraph, Text } = Typography;

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

type GoogleMapsPlaces = {
  Autocomplete: new (
    input: HTMLInputElement,
    opts?: { types?: string[]; fields?: string[] }
  ) => {
    addListener: (event: string, handler: () => void) => void;
    getPlace: () => { place_id?: string; name?: string; formatted_address?: string };
  };
};

type GoogleMapsApi = {
  maps: { places: GoogleMapsPlaces };
};

declare global {
  interface Window {
    google?: GoogleMapsApi;
    [key: string]: unknown;
  }
}

function loadGoogleMapsScript(apiKey: string): Promise<void> {
  if (window.google?.maps?.places) {
    return Promise.resolve();
  }

  const existing = document.querySelector(
    'script[data-google-maps="places"]'
  ) as HTMLScriptElement | null;
  if (existing) {
    return new Promise((resolve, reject) => {
      if (window.google?.maps?.places) {
        resolve();
        return;
      }

      const onReady = () => {
        if (window.google?.maps?.places) {
          resolve();
        } else {
          reject(new Error("Failed to load Google Maps"));
        }
      };

      existing.addEventListener("load", onReady, { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Failed to load Google Maps")),
        { once: true }
      );
    });
  }

  return new Promise((resolve, reject) => {
    const callbackName = `__googleMapsPlacesCb_${Date.now()}`;

    window[callbackName] = () => {
      delete window[callbackName];
      if (window.google?.maps?.places) {
        resolve();
      } else {
        reject(new Error("Failed to load Google Maps"));
      }
    };

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&loading=async&callback=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMaps = "places";
    script.onerror = () => {
      delete window[callbackName];
      reject(new Error("Failed to load Google Maps"));
    };
    document.head.appendChild(script);
  });
}

const GoogleReviewLinkGenerator: React.FC = () => {
  const [activeTab, setActiveTab] = useState("search");
  const [email, setEmail] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBusiness, setSelectedBusiness] = useState<{
    placeId: string;
    businessName?: string;
  } | null>(null);
  const [mapsUrl, setMapsUrl] = useState("");
  const [placeIdInput, setPlaceIdInput] = useState("");
  const [result, setResult] = useState<ReviewLinkResult | null>(null);
  const [generating, setGenerating] = useState(false);
  const [placesReady, setPlacesReady] = useState(false);
  const [mapsLoadError, setMapsLoadError] = useState<string | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<InstanceType<GoogleMapsPlaces["Autocomplete"]> | null>(null);
  const qrCanvasRef = useRef<HTMLDivElement>(null);

  const registerAndApplyResult = useCallback(
    async (next: ReviewLinkResult) => {
      const trimmedEmail = email.trim();
      if (!trimmedEmail) {
        message.warning("Enter your email to generate a review link.");
        return;
      }

      setGenerating(true);
      const hideLoading = message.loading("Creating your review link...", 0);
      try {
        const response = await api.post<{ data: FreeRegisterData }>("/v1/free-register/", {
          email: trimmedEmail,
          guide_link: next.reviewUrl,
          name: next.businessName || "Main Link",
        });
        const videoLink = response.data?.data?.video_link;
        if (!videoLink) {
          throw new Error("No guide link returned");
        }
        setResult({ ...next, videoLink: videoLink });
        message.success("Review link generated!");
      } catch (err) {
        const error = err as {
          response?: { data?: Record<string, string | string[]> };
        };

        const data = error.response?.data;
        if (data === undefined) {
          message.error("Could not generate review link. Please try again.");
          setResult(null);
          return;
        }

        const apiError =
          (typeof data?.error === "string" ? data.error : null) ||
          (Array.isArray(data?.email) ? data.email[0] : null) ||
          (Array.isArray(data?.guide_link) ? data.guide_link[0] : null) ||
          (typeof data?.email === "string" ? data.email : null) ||
          (typeof data?.guide_link === "string" ? data.guide_link : null);
        message.error(apiError || "Could not generate review link. Please try again.");
        setResult(null);
      } finally {
        hideLoading();
        setGenerating(false);
      }
    },
    [email]
  );

  const handleResolveInput = useCallback(
    async (value: string) => {
      const resolved = resolveReviewLinkFromInput(value);
      if (resolved.kind === "error") {
        message.error(resolved.message);
        setResult(null);
        return;
      }
      await registerAndApplyResult(resolved);
    },
    [registerAndApplyResult]
  );

  const handlePasteUrl = () => handleResolveInput(mapsUrl);
  const handlePlaceId = () => handleResolveInput(placeIdInput);

  const handleSearchGenerate = () => {
    if (!selectedBusiness) {
      message.warning("Pick your business from the dropdown suggestions.");
      return;
    }
    void registerAndApplyResult({
      kind: "place_id",
      placeId: selectedBusiness.placeId,
      reviewUrl: buildGoogleReviewLink(selectedBusiness.placeId),
      businessName: selectedBusiness.businessName,
    });
  };

  const handleMapsSearch = () => {
    if (!searchQuery.trim()) {
      message.warning("Enter your business name and location.");
      return;
    }
    window.open(buildMapsSearchUrl(searchQuery.trim()), "_blank", "noopener,noreferrer");
    message.info(
      "Open your business in Google Maps, click Share, copy the link, then paste it under the Maps URL tab."
    );
  };

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) return;

    let cancelled = false;
    loadGoogleMapsScript(GOOGLE_MAPS_API_KEY)
      .then(() => {
        if (!cancelled) setPlacesReady(true);
      })
      .catch((err) => {
        console.error("Failed to load Google Maps:", err);
        if (!cancelled) {
          setMapsLoadError("Google Places could not be loaded. Use the Maps URL or Place ID tab.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!placesReady || !searchInputRef.current || !window.google?.maps?.places) {
      return;
    }

    const autocomplete = new window.google.maps.places.Autocomplete(
      searchInputRef.current,
      { types: ["establishment"], fields: ["place_id", "name", "formatted_address"] }
    );

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      if (!place.place_id) {
        message.error("No Place ID for this result. Try another business or paste a Maps URL.");
        setSelectedBusiness(null);
        return;
      }
      const businessName = place.name || place.formatted_address;
      setSelectedBusiness({
        placeId: place.place_id,
        businessName,
      });
      if (businessName) {
        setSearchQuery(businessName);
        if (searchInputRef.current) {
          searchInputRef.current.value = businessName;
        }
      }
    });

    autocompleteRef.current = autocomplete;
    return () => {
      autocompleteRef.current = null;
    };
  }, [placesReady]);

  const copyReviewLink = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.reviewUrl);
      message.success("Review link copied!");
    } catch {
      message.error("Could not copy. Select the link and copy manually.");
    }
  };

  const downloadQrCode = () => {
    const svg = qrCanvasRef.current?.querySelector(".google-review-qr-svg");
    if (!svg || !result) {
      message.error("Generate a review link first.");
      return;
    }
    downloadQrCodePngFromSvg(
      svg as SVGElement,
      result.reviewUrl,
      "google-review-qr.png"
    );
  };

  const searchTab = (
    <div className="space-y-4">
      {GOOGLE_MAPS_API_KEY && placesReady ? (
        <>
          <label className="block text-sm font-medium text-slate-700">
            Search for your business
          </label>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="e.g. Joe's Coffee, Edmonton"
            className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-900 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
            defaultValue={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSelectedBusiness(null);
            }}
          />
          <Paragraph type="secondary" className="!mb-0 text-sm">
            Pick your business from the dropdown, then click Generate review link.
          </Paragraph>
          <Button
            type="primary"
            size="large"
            loading={generating}
            onClick={handleSearchGenerate}
            className="!bg-cyan-600 hover:!bg-cyan-500"
          >
            Generate review link
          </Button>
        </>
      ) : (
        <>
          <label className="block text-sm font-medium text-slate-700">
            Business name & location
          </label>
          <Input
            size="large"
            placeholder="e.g. Joe's Coffee, Edmonton"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onPressEnter={handleMapsSearch}
          />
          {mapsLoadError && (
            <Text type="warning" className="text-sm">
              {mapsLoadError}
            </Text>
          )}
          {!GOOGLE_MAPS_API_KEY && (
            <Text type="secondary" className="block text-sm">
              Add <code className="text-xs">REACT_APP_GOOGLE_MAPS_API_KEY</code> for in-page
              search, or find your business on Google Maps and paste the share link below.
            </Text>
          )}
          <Button
            type="primary"
            icon={<SearchOutlined />}
            size="large"
            onClick={handleMapsSearch}
            className="!bg-cyan-600 hover:!bg-cyan-500"
          >
            Find on Google Maps
          </Button>
        </>
      )}
    </div>
  );

  const urlTab = (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-slate-700">
        Paste your Google Maps or review link
      </label>
      <Input.TextArea
        rows={3}
        placeholder="https://www.google.com/maps/place/..."
        value={mapsUrl}
        onChange={(e) => setMapsUrl(e.target.value)}
      />
      <Button
        type="primary"
        size="large"
        loading={generating}
        onClick={handlePasteUrl}
        className="!bg-cyan-600 hover:!bg-cyan-500"
      >
        Generate review link
      </Button>
    </div>
  );

  const placeIdTab = (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-slate-700">
        Google Place ID
      </label>
      <Input
        size="large"
        placeholder="ChIJ..."
        value={placeIdInput}
        onChange={(e) => setPlaceIdInput(e.target.value)}
        onPressEnter={handlePlaceId}
      />
      <Paragraph type="secondary" className="!mb-0 text-sm">
        Find your Place ID with{" "}
        <a
          href="https://developers.google.com/maps/documentation/javascript/examples/places-placeid-finder"
          target="_blank"
          rel="noopener noreferrer"
          className="text-cyan-700 underline"
        >
          Google&apos;s Place ID Finder
        </a>
        .
      </Paragraph>
      <Button
        type="primary"
        size="large"
        loading={generating}
        onClick={handlePlaceId}
        className="!bg-cyan-600 hover:!bg-cyan-500"
      >
        Generate review link
      </Button>
    </div>
  );

  return (
    <div
      className="min-h-screen"
      style={{
        background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
      }}
    >
      <Header />
      <main className="mx-auto max-w-3xl px-4 pb-16 pt-24 sm:px-6">
        <div className="mb-10 text-center">
          <p className="mb-2 text-sm font-semibold uppercase tracking-widest text-cyan-400">
            Free tool
          </p>
          <Title level={1} className="!mb-3 !text-white">
            Vioo Review Video Link Generator
          </Title>
          <Paragraph className="!mx-auto !max-w-xl !text-slate-300 !text-lg">
            Generate your unique review video link and QR code, then share them with customers
            to grow your reviews.
          </Paragraph>
        </div>

        <div className="rounded-2xl bg-white p-6 shadow-xl sm:p-8">
          <div className="mb-6 space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Email address
            </label>
            <Input
              size="large"
              type="email"
              placeholder="you@business.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Paragraph type="secondary" className="!mb-0 text-sm">
              We will create your free Vioo review page and send login details to this email.
            </Paragraph>
          </div>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              { key: "search", label: "Find business", children: searchTab },
              { key: "url", label: "Maps URL", children: urlTab },
              { key: "placeid", label: "Place ID", children: placeIdTab },
            ]}
          />
        </div>

        {result && (
          <div className="mt-8 space-y-6 rounded-2xl bg-white p-6 shadow-xl sm:p-8">
            {result.businessName && (
              <div>
                <Text type="secondary">Business</Text>
                <Title level={4} className="!mb-0 !mt-1">
                  {result.businessName}
                </Title>
              </div>
            )}

            <div>
              <Text type="secondary" className="block mb-2">
                Your Vioo Review video link
              </Text>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Input
                  readOnly
                  size="large"
                  value={result.videoLink}
                  addonAfter={
                    <a
                      href={result.videoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Open review link"
                    >
                      <LinkOutlined />
                    </a>
                  }
                />
                <Button
                  type="primary"
                  size="large"
                  icon={<CopyOutlined />}
                  onClick={copyReviewLink}
                  className="shrink-0 !bg-cyan-600 hover:!bg-cyan-500"
                >
                  Copy link
                </Button>
              </div>
              {result.kind === "place_id" && (
                <Text type="secondary" className="mt-2 block text-xs">
                  Place ID: {result.placeId}
                </Text>
              )}
            </div>

            <div className="flex flex-col items-center border-t border-slate-100 pt-8">
              <Text type="secondary" className="mb-4">
                QR code — print or share with customers
              </Text>
              <div
                ref={qrCanvasRef}
                className="rounded-2xl border border-cyan-200/60 bg-gradient-to-br from-teal-50 via-slate-50 to-cyan-50 p-5 shadow-lg shadow-cyan-900/10"
              >
                <QRCodeSVG
                  value={result.reviewUrl}
                  size={256}
                  level="H"
                  includeMargin
                  fgColor={QR_CODE_STYLE.fgColor}
                  bgColor={QR_CODE_STYLE.bgColor}
                  className="google-review-qr-svg rounded-xl"
                  style={{ width: "256px", height: "256px" }}
                />
              </div>
              <a
                href={result.videoLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 max-w-md break-all text-center text-sm font-medium text-slate-600 underline decoration-cyan-400/60 underline-offset-2 hover:text-cyan-700"
              >
                {result.videoLink}
              </a>
              <Button
                className="mt-4"
                icon={<DownloadOutlined />}
                onClick={downloadQrCode}
              >
                Download QR code
              </Button>
            </div>
          </div>
        )}

        <section className="mt-12 rounded-2xl border border-slate-700/60 bg-slate-800/40 p-6 text-slate-300">
          <Title level={4} className="!text-white !mb-4">
            How it works
          </Title>
          <ol className="list-decimal space-y-2 pl-5 text-sm leading-relaxed">
            <li>Find your business using search, or paste the link from Google Maps Share.</li>
            <li>We build a direct link to leave a review on Google.</li>
            <li>Copy the link or download the QR code for signs, receipts, and email.</li>
          </ol>
          <p className="mt-6 text-sm">
            Powered by{" "}
            <Link to="/" className="text-cyan-400 hover:text-cyan-300">
              Vioo.vip
            </Link>
            .
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default GoogleReviewLinkGenerator;
